// Admin-only response shapes that don't naturally belong to a feature module.

import { z } from 'zod';
import { uuid, isoDatetime } from './common.js';

export const UnitType = {
  HOUSE: 'HOUSE',
  APARTMENT: 'APARTMENT',
  RUKO: 'RUKO',
} as const;
export type UnitType = (typeof UnitType)[keyof typeof UnitType];

export const OccupancyStatus = {
  OCCUPIED: 'OCCUPIED',
  VACANT: 'VACANT',
  OWNER_ABSENT: 'OWNER_ABSENT',
} as const;
export type OccupancyStatus = (typeof OccupancyStatus)[keyof typeof OccupancyStatus];

// Slim unit shape for the admin "pick a unit" picker. Drops cluster_id,
// notes, lat/lng/size_m2/street/timestamps — those belong to a later
// detail endpoint when Phase 5c (units browser) ships.
export const adminUnit = z.object({
  id: uuid,
  block: z.string(),
  number: z.string(),
  label: z.string(),
  type: z.enum([UnitType.HOUSE, UnitType.APARTMENT, UnitType.RUKO]),
  occupancy: z.enum([
    OccupancyStatus.OCCUPIED,
    OccupancyStatus.VACANT,
    OccupancyStatus.OWNER_ABSENT,
  ]),
});
export type AdminUnit = z.infer<typeof adminUnit>;

export const listAdminUnitsResponse = z.object({
  units: z.array(adminUnit),
});
export type ListAdminUnitsResponse = z.infer<typeof listAdminUnitsResponse>;

const unitTypeEnum = z.enum([UnitType.HOUSE, UnitType.APARTMENT, UnitType.RUKO]);
const occupancyEnum = z.enum([
  OccupancyStatus.OCCUPIED,
  OccupancyStatus.VACANT,
  OccupancyStatus.OWNER_ABSENT,
]);

// Create a unit. Address parts follow the cluster's scheme (street optional,
// block toggleable, number always required); presence is enforced server-side
// via checkAddressParts. `label` defaults to the space-joined parts when blank.
export const createAdminUnitRequest = z.object({
  street: z.string().max(20).trim().optional(),
  block: z.string().max(20).trim().optional(),
  number: z.string().min(1).max(20).trim(),
  label: z.string().max(60).trim().optional(),
  type: unitTypeEnum.default(UnitType.HOUSE),
  occupancy: occupancyEnum.default(OccupancyStatus.OCCUPIED),
});
export type CreateAdminUnitRequest = z.infer<typeof createAdminUnitRequest>;

// Edit a unit. Block/number are identity (the unique key) and aren't editable
// here — delete + recreate if they're wrong.
export const updateAdminUnitRequest = z.object({
  label: z.string().max(60).trim().optional(),
  type: unitTypeEnum.optional(),
  occupancy: occupancyEnum.optional(),
});
export type UpdateAdminUnitRequest = z.infer<typeof updateAdminUnitRequest>;

// ─── Staff & security accounts (Phase 12) ──────────────────────────────────
// Two independent permission-flag sets — see docs/07-staff-permission-presets.md.
export const STAFF_PERMISSION_FLAGS = [
  'can_manage_staff',
  'can_manage_units',
  'can_manage_residents',
  'can_manage_invite_codes',
  'can_post_announcements',
  'can_manage_facilities',
  'can_approve_bookings',
  'can_manage_payments',
  'can_manage_complaints',
  'can_view_emergency',
] as const;
export const SECURITY_PERMISSION_FLAGS = [
  'can_scan_visitors',
  'can_log_packages',
  'can_respond_emergency',
] as const;

export const StaffRole = { STAFF: 'STAFF', SECURITY: 'SECURITY' } as const;
const staffRoleEnum = z.enum([StaffRole.STAFF, StaffRole.SECURITY]);

export const publicStaffUser = z.object({
  id: uuid,
  email: z.string(),
  full_name: z.string(),
  role: staffRoleEnum,
  status: z.enum(['ACTIVE', 'SUSPENDED', 'PENDING']),
  permissions: z.array(z.string()), // truthy flag names
  created_at: isoDatetime,
});
export type PublicStaffUser = z.infer<typeof publicStaffUser>;

// ─── Residents (Phase 12) ──────────────────────────────────────────────────
// RESIDENT users in the cluster. Unit fields are null for PENDING residents
// (signed up but not yet linked to a unit via an invite code).
export const publicResident = z.object({
  id: uuid,
  full_name: z.string(),
  email: z.string(),
  status: z.enum(['ACTIVE', 'PENDING', 'SUSPENDED']),
  unit_block: z.string().nullable(),
  unit_number: z.string().nullable(),
  unit_label: z.string().nullable(),
  is_primary: z.boolean().nullable(),
  created_at: isoDatetime,
});
export type PublicResident = z.infer<typeof publicResident>;

export const listResidentsResponse = z.object({ items: z.array(publicResident) });
export type ListResidentsResponse = z.infer<typeof listResidentsResponse>;

export const updateResidentRequest = z.object({
  status: z.enum(['ACTIVE', 'SUSPENDED']),
});
export type UpdateResidentRequest = z.infer<typeof updateResidentRequest>;

// Promote one resident to primary for a unit (demotes the current primary).
export const setPrimaryResidentRequest = z.object({
  unit_id: uuid,
  user_id: uuid,
});
export type SetPrimaryResidentRequest = z.infer<typeof setPrimaryResidentRequest>;
